<template>
  <div class="knowledge-page">
    <header>
      <div class="topbar">
        <div>
          <span>WEALTH INSIGHTS</span>
          <h1>资讯与产品指南</h1>
        </div>
      </div>
      <p>看懂产品周期、返利方式、结算规则与本息保障权益。</p>
      <div class="search-box">
        <span>⌕</span>
        <input v-model="keyword" placeholder="搜索资讯、产品规则或权益说明" />
      </div>
    </header>

    <nav class="tabs">
      <button
        v-for="tab in tabs"
        :key="tab.key"
        :class="{ active: activeTab === tab.key }"
        @click="activeTab = tab.key"
      >
        {{ tab.name }}
      </button>
    </nav>

    <main>
      <section v-if="activeTab === 'news' || activeTab === 'report'" class="article-list">
        <article
          v-for="(item, index) in filteredArticles"
          :key="item.id || item.title"
          @click="showArticle(item)"
        >
          <div :class="['cover', item.tone, { 'has-image': item.image }]" :style="item.image ? { backgroundImage: 'url(' + item.image + ')' } : null">
            <span>{{ item.category }}</span>
            <strong>0{{ index + 1 }}</strong>
          </div>
          <div class="article-copy">
            <div class="article-meta">
              <span>{{ item.category }}</span>
              <small class="article-views">◉ {{ formatViewCount(item.viewCount) }}人浏览</small>
              <small>{{ item.date }}</small>
            </div>
            <h2>{{ item.title }}</h2>
            <p>{{ item.summary }}</p>
            <small>阅读详情 · ASM太平洋研究院</small>
          </div>
        </article>
      </section>

      <section v-if="activeTab === 'course'" class="course-section">
        <div class="progress-card">
          <span>新手成长路径</span>
          <h2>从认识风险开始，完成你的第一份资产配置</h2>
          <div><i :style="{ width: courseProgress + '%' }"></i></div>
          <p>已学习 {{ learnedCount }}/{{ courses.length }} 节</p>
        </div>
        <article
          v-for="(course, index) in courses"
          :key="course.title"
          class="course-card"
          @click="learnCourse(index, course)"
        >
          <span class="course-no">0{{ index + 1 }}</span>
          <div>
            <small>{{ course.level }} · {{ course.duration }}</small>
            <h2>{{ course.title }}</h2>
            <p>{{ course.desc }}</p>
          </div>
          <b>{{ learned.includes(index) ? "✓" : "›" }}</b>
        </article>
      </section>

      <section v-if="activeTab === 'risk'" class="risk-section">
        <div class="risk-hero">
          <span>PRODUCT INFORMATION</span>
          <h2>了解产品，是确认购买前的重要一步</h2>
          <p>请重点核对产品周期、返利方式、到期安排及本金与约定收益保障范围。</p>
        </div>
        <article v-for="risk in risks" :key="risk.id || risk.title" @click="showArticle(risk)">
          <span>{{ risk.icon }}</span>
          <div>
            <h3>{{ risk.title }}</h3>
            <p>{{ risk.content }}</p>
            <small>{{ risk.tip }}</small>
          </div>
        </article>
        <div class="suitability">
          <h3>实名签约与合同说明</h3>
          <p>首次购买前需完成实名认证；本人电子签名在购买产品时填写。购买成功后，一笔订单生成一份本金及约定收益保障电子合同，可在“我的产品”查看保障期限、返利方式和到期权益。</p>
          <button @click="$router.push('/memmber_business')">完善实名认证 →</button>
        </div>
      </section>
    </main>

    <div v-if="activeArticle" class="reader" @click.self="activeArticle = null">
      <article>
        <button @click="activeArticle = null">×</button>
        <span>{{ activeArticle.category }} · {{ activeArticle.date }}</span>
        <h1>{{ activeArticle.title }}</h1>
        <p class="lead">{{ activeArticle.summary }}</p>
        <img v-if="activeArticle.image" class="reader-cover" :src="activeArticle.image" :alt="activeArticle.title" />
        <video v-if="activeArticle.video" class="reader-video" :src="activeArticle.video" controls playsinline></video>
        <div v-if="activeArticle.contentHtml" class="reader-rich-content" v-html="activeArticle.contentHtml"></div>
        <template v-else>
          <p v-for="paragraph in activeArticle.content" :key="paragraph">{{ paragraph }}</p>
        </template>
      </article>
    </div>
  </div>
</template>

<script>
import request from "@utils/request";

const DEFAULT_ARTICLES = [
  {
    type: "news", category: "股市要闻", tone: "navy", date: "2026.07.28", read: "5分钟",
    title: "A股双创指数集体大跌，算力硬件股重挫，光刻机概念逆市爆发",
    summary: "创业板指收跌超7%，科创50跌超5%。兆易创新、东山精密等跌停，机构认为A股不具备走熊基础。",
    content: [
      "今日A股市场大幅调整，双创指数集体大跌。创业板指收跌超7%，科创50跌超5%，市场恐慌情绪蔓延。",
      "【板块表现】算力硬件股重挫，兆易创新（2700亿市值）、东山精密（3500亿市值）等跌停。光刻机概念逆市爆发，成为少数上涨板块。",
      "【机构观点】多家机构认为A股不具备走熊基础，政策底已显现。短期调整更多是情绪面和技术面因素导致，中长期来看市场估值已处于历史低位区间。",
      "【外围市场】韩国股市熔断，SK海力士跌超13%，半导体抛售潮蔓延至亚太市场。韩股收盘暴跌逾10%，盘中一度跌破5900点。"
    ]
  },
  {
    type: "news", category: "环球市场", tone: "blue", date: "2026.07.28", read: "4分钟",
    title: "苹果市值首次突破5万亿美元，成为全球第二家达此里程碑的上市公司",
    summary: "苹果股价盘初上涨1%，成为继英伟达之后全球第二家市值突破5万亿美元的上市公司。",
    content: [
      "苹果公司股价今日盘初上涨1%，市值首次突破5万亿美元大关，成为继英伟达之后全球第二家达到此里程碑的上市公司。",
      "【市场表现】英伟达当前市值约4.7万亿美元，苹果此次突破5万亿进一步巩固了其在科技股中的领先地位。两家公司合计市值接近10万亿美元。",
      "【行业背景】与此同时，纳指100指数即将进入回调，自峰值下跌10%仅耗时38个交易日，科技股整体承压。费城半导体指数开盘下跌3.5%，美光、AMD等跌幅居前。",
      "【投资提示】科技股估值分化加剧，投资者需关注个股基本面与估值的匹配程度，避免追高。"
    ]
  },
  {
    type: "report", category: "研报解读", tone: "purple", date: "2026.07.24", read: "8分钟",
    title: "下半年宏观展望：增长、利率与市场机会",
    summary: "梳理影响全球市场的主要变量，建立清晰而可复用的观察框架。",
    content: ["宏观研究的价值不在于给出唯一答案，而在于识别可能影响资产价格的关键变量和传导路径。", "观察增长、通胀、政策和流动性时，需要结合市场估值与投资者预期，避免只根据单一指标决策。", "在不确定环境中，情景分析和风险预算通常比方向性押注更重要。"]
  },
  {
    type: "news", category: "宏观经济", tone: "navy", date: "2026.07.28", read: "6分钟",
    title: "国际油价重挫，国内能化期货显著降温，特朗普称停火给伊朗谈判机会",
    summary: "国际油价大幅下跌，国内能化期货随之降温。机构连发12道风险提示，多空博弈激烈。",
    content: [
      "国际油价今日重挫，特朗普称停火给伊朗谈判机会，市场对供应端紧张的担忧有所缓解，油价应声暴跌。",
      "【市场反应】国内能化期货显著降温，多家机构连续发布12道风险提示，多空双方博弈激烈。分析师指出，地缘政治因素仍是影响油价的核心变量。",
      "【关联影响】油价下跌对下游行业形成利好，交通运输、化工等板块成本压力有望减轻。但能源股短期承压，投资者需注意板块轮动节奏。",
      "【后市展望】市场对OPEC+后续产量决策保持关注，油价短期波动可能加大，建议投资者控制仓位风险。"
    ]
  },
  {
    type: "news", category: "市场数据", tone: "blue", date: "2026.07.27", read: "4分钟",
    title: "上半年银行理财平均收益率2.05%，存续规模33.66万亿元",
    summary: "银行理财市场整体稳健，混合类产品规模突破万亿元，投资者风险偏好有所回升。",
    content: [
      "最新数据显示，2026年上半年银行理财平均收益率为2.05%，6月末存续规模达33.66万亿元，同比增加9.75%。",
      "【产品结构】混合类产品规模突破万亿元，显示投资者风险偏好有所回升。固定收益类产品仍占主导地位，但权益类资产配置比例逐步提升。",
      "【趋势分析】在利率下行背景下，银行理财收益率整体走低，但相比存款仍有一定优势。投资者应关注产品底层资产质量，避免盲目追求高收益。",
      "【配置建议】建议投资者根据自身风险承受能力，合理配置现金管理类、固收类和混合类产品，实现收益与风险的平衡。"
    ]
  },
  {
    type: "news", category: "产业经济", tone: "navy", date: "2026.07.27", read: "5分钟",
    title: "2026《财富》世界500强出炉：亚马逊登顶，中国122家企业上榜",
    summary: "亚马逊终结沃尔玛12连冠，中国上榜企业数量保持全球第二，反映经济结构持续优化。",
    content: [
      "2026年《财富》世界500强榜单正式出炉，亚马逊登顶榜首，终结了沃尔玛连续12年的冠军地位。中国共有122家企业上榜，数量保持全球第二。",
      "【榜单亮点】科技企业排名普遍上升，反映数字经济在全球经济中的比重持续增加。传统能源企业排名有所下滑，能源转型趋势明显。",
      "【中国表现】中国上榜企业集中在银行、能源、建筑和科技领域。新能源、人工智能等新兴领域企业排名提升较快，反映中国经济结构持续优化。",
      "【行业启示】世界500强榜单的变化反映了全球经济格局的深刻调整，投资者可关注上榜企业的行业分布变化，把握中长期投资方向。"
    ]
  },
  {
    type: "news", category: "政策监管", tone: "blue", date: "2026.07.26", read: "4分钟",
    title: "商务部回应美301调查：不能以产能问题为名行保护主义之实",
    summary: "商务部表示中国从不刻意追求贸易顺差，反对单边主义贸易保护措施。",
    content: [
      "商务部今日回应美国针对产能问题发起的301调查，表示不能以产能问题为名行保护主义之实，中国从不刻意追求贸易顺差。",
      "【政策立场】中方认为，全球产业链供应链的形成是市场规律和企业选择的结果，单边主义贸易保护措施不利于全球经济复苏。",
      "【市场影响】贸易摩擦相关行业短期可能面临不确定性，但中国企业已具备较强的抗风险能力。建议投资者关注内需驱动型行业，降低外部风险敞口。",
      "【后续关注】商务部表示将继续通过对话协商解决贸易分歧，维护多边贸易体制。"
    ]
  },
  {
    type: "report", category: "策略研究", tone: "gold", date: "2026.07.22", read: "7分钟",
    title: "从目标出发：家庭财富的四账户配置方法",
    summary: "将日常保障、稳健增值、长期成长与传承目标分别管理。",
    content: ["不同用途的资金应匹配不同的风险与期限。日常支出和紧急备用金首先强调流动性，长期资金则可以承受更长周期的波动。", "四账户方法是一种梳理目标的框架，并非固定比例。实际配置仍需��合家庭收支、负债和风险承受能力。"]
  },
  {
    type: "report", category: "公司研究", tone: "purple", date: "2026.07.28", read: "10分钟",
    title: "ASMPT未来展望：AI驱动先进封装，开启新一轮增长周期",
    summary: "从AI芯片需求、先进封装技术路线和全球产能布局三个维度，分析ASMPT的未来发展前景。",
    content: [
      "ASMPT作为全球领先的半导体和电子制造解决方案供应商，正站在AI浪潮与先进封装技术革命的历史交汇点。公司凭借在热压键合（TCB）、混合键合（Hybrid Bonding）和激光划片等核心技术的深厚积累，已成为AI芯片封装领域的关键设备供应商。",
      "【AI驱动增长】随着AI大模型和高性能计算（HPC）需求的爆发式增长，先进封装市场正进入高速扩张期。ASMPT的TCB工具已获得领先逻辑半导体制造商的重复订单，为AI芯片的量产提供了关键技术支持。高带宽存储（HBM）的渗透率提升进一步拉动了公司的封装设备需求。",
      "【技术路线图】公司成立技术咨询委员会（TAC），汇聚行业与学术领袖，为先进封装路线图提供战略建议。在3D-IC异构集成、共封装光学（Co-Packaged Optics）和微转印技术（MTP）等前沿领域持续投入，确保技术领先地位。",
      "【全球布局】ASMPT在全球设有多个研发中心和生产基地，覆盖亚洲、欧洲与美洲。2023年成立中国子公司奥芯明（AoXinMing），深化本土化服务能力。2025年庆祝成立50周年，发布50周年纪念电子书，彰显公司长期稳健的发展战略。",
      "【财务展望】凭借在半导体和SMT两大市场的领先地位，以及AI、汽车电子和工业4.0等新兴领域的增长机遇，ASMPT有望在未来几年实现持续增长。公司持续加大研发投入，研发人员占比超过30%，为长期竞争力奠定坚实基础。"
    ]
  }
];

const DEFAULT_COURSES = [
  { level: "入门", duration: "8分钟", title: "认识定期返利产品", desc: "理解本金、产品周期、返利比例和到期日。" },
  { level: "入门", duration: "10分钟", title: "读懂返利与结算方式", desc: "区分按日返息到期返本与到期返本返息。" },
  { level: "进阶", duration: "12分钟", title: "查看电子保障合同", desc: "了解合同编号、电子签名、保障期限和本息权益。" },
  { level: "进阶", duration: "9分钟", title: "核对账户返利记录", desc: "学会核对已返金额、待返金额与到期结算记录。" }
];

const DEFAULT_RISKS = [
  { icon: "期", title: "产品周期", content: "购买生效后按合同约定周期执行，到期前一般不支持提前取消。", tip: "重点查看：起息日、产品天数、到期日" },
  { icon: "返", title: "返利方式", content: "不同产品采用按日返息到期返本，或到期一次性返本返息。", tip: "重点查看：日返比例、到账账户、结算时间" },
  { icon: "保", title: "合同保障权益", content: "每笔成功购买对应独立的本金及约定收益保障合同，保障内容以订单合同展示为准。", tip: "重点查看：合同编号、保障本金、收益率、保障期限" },
  { icon: "签", title: "电子签名", content: "购买产品时填写的本人手写签名会固化到该笔订单合同。", tip: "重点查看：签署人、签署时间、合同状态" }
];

export default {
  name: "KnowledgeHub",
  data() {
    return {
      keyword: "",
      activeTab: this.$route.query.tab || "news",
      activeArticle: null,
      learned: [],
      articles: DEFAULT_ARTICLES,
      tabs: [
        { key: "news", name: "财经新闻" },
        { key: "report", name: "研报解读" },
        { key: "course", name: "产品课程" },
        { key: "risk", name: "产品与保障" }
      ],
      courses: DEFAULT_COURSES,
      risks: DEFAULT_RISKS
    };
  },
  computed: {
    filteredArticles() {
      const keyword = this.keyword.trim();
      return this.articles.filter(item => {
        const typeMatch = item.type === this.activeTab;
        const keywordMatch = !keyword || (item.title + item.summary + item.category).includes(keyword);
        return typeMatch && keywordMatch;
      });
    },
    learnedCount() {
      return this.learned.length;
    },
    courseProgress() {
      return this.courses.length ? Math.round((this.learnedCount / this.courses.length) * 100) : 0;
    }
  },
  mounted() {
    this.loadKnowledge();
  },
  watch: {
    activeTab(tab) {
      this.$router.replace({ path: "/knowledge", query: { tab } });
    },
    "$route.query.tab"(tab) {
      if (tab) this.activeTab = tab;
    }
  },
  methods: {
    showArticle(item) {
      this.recordView(item);
      if (item.contentHtml || Array.isArray(item.content)) {
        this.activeArticle = item;
        return;
      }
      const isCourse = Object.prototype.hasOwnProperty.call(item, "level");
      this.activeArticle = Object.assign({}, item, {
        category: isCourse ? "产品课程" : "产品与保障",
        date: "",
        summary: item.desc || item.content || "",
        content: isCourse
          ? ["课程要点：购买前核对产品周期和返利方式；购买后在“我的产品”核对合同、本人签名、保障期限和每期返利记录。"]
          : [item.tip || ""]
      });
    },
    learnCourse(index, course) {
      if (!this.learned.includes(index)) this.learned.push(index);
      this.showArticle(course);
    },
    normalizeItem(item, index) {
      const tones = ["navy", "blue", "purple", "gold"];
      const common = {
        id: item.id,
        type: item.section,
        category: item.label || this.tabs.find(tab => tab.key === item.section).name,
        tone: tones[index % tones.length],
        date: item.date || "",
        read: item.extra || "详情",
        title: item.title,
        summary: item.summary || "",
        image: item.image || "",
        video: item.video || "",
        contentHtml: item.content || "",
        content: [],
        viewCount: Number(item.view_count || 1000)
      };
      if (item.section === "course") {
        return Object.assign(common, { level: item.label || "课程", duration: item.extra || "", desc: item.summary || "" });
      }
      if (item.section === "risk") {
        return Object.assign(common, { icon: item.icon || "阅", content: item.summary || "", tip: item.extra || "" });
      }
      return common;
    },
    formatViewCount(value) {
      const count = Math.max(1000, Number(value) || 1000);
      return Math.floor(count).toLocaleString("en-US");
    },
    recordView(item) {
      if (!item) return;
      if (!item.id) {
        this.$set(item, "viewCount", Math.max(1000, Number(item.viewCount) || 1000) + 1);
        return;
      }
      request
        .post("/api/v1/knowledge/content/view", { id: item.id }, { login: false })
        .then(response => {
          const data = response && response.data && response.data.data;
          if (data && data.view_count) this.$set(item, "viewCount", Number(data.view_count));
        })
        .catch(() => {});
    },
    loadKnowledge() {
      request.post("/api/v1/knowledge/content/list", {}, { login: false }).then(response => {
        const rows = response && response.data && response.data.data;
        if (!Array.isArray(rows) || !rows.length) return;
        const grouped = rows.reduce((result, item, index) => {
          if (!result[item.section]) result[item.section] = [];
          result[item.section].push(this.normalizeItem(item, index));
          return result;
        }, {});
        if (grouped.news || grouped.report) {
          const dynamicArticles = (grouped.news || []).concat(grouped.report || []);
          const publishedTitles = dynamicArticles.map(item => item.title);
          this.articles = dynamicArticles.concat(
            DEFAULT_ARTICLES.filter(item => !publishedTitles.includes(item.title))
          );
        }
        if (grouped.course) this.courses = grouped.course;
        if (grouped.risk) this.risks = grouped.risk;
      }).catch(() => {});
    }
  }
};
</script>

<style scoped lang="less">
.knowledge-page { width: 100%; max-width: 100%; min-height: 100vh; padding-bottom: 76px; overflow-x: hidden; color: #1d2a40; background: #f4f7fb; }
.knowledge-page,.knowledge-page * { box-sizing: border-box; }
button,input { font: inherit; border: 0; background: none; }
header { padding: 23px 16px 26px; color: #fff; background: radial-gradient(circle at 85% 5%,rgba(99,170,242,.35),transparent 30%),linear-gradient(145deg,#071e49,#0d4b8c); border-radius: 0 0 26px 26px; }
.topbar { display: flex; align-items: flex-start; justify-content: space-between; }
.topbar span { color: #83b9ee; font-size: 8px; font-weight: 700; letter-spacing: 1.3px; }
.topbar h1 { margin-top: 4px; font-size: 25px; }
.topbar button { padding: 7px 10px; color: #dcecff; font-size: 9px; border: 1px solid rgba(255,255,255,.18); border-radius: 12px; }
header > p { margin-top: 9px; color: rgba(255,255,255,.66); font-size: 10px; }
.search-box { display: flex; align-items: center; gap: 9px; margin-top: 20px; padding: 0 13px; background: rgba(255,255,255,.12); border: 1px solid rgba(255,255,255,.13); border-radius: 13px; }
.search-box span { font-size: 18px; }
.search-box input { width: 100%; height: 43px; color: #fff; outline: none; }
.search-box input::placeholder { color: rgba(255,255,255,.5); font-size: 10px; }
.tabs { position: sticky; z-index: 10; top: 0; display: grid; grid-template-columns: repeat(4,minmax(0,1fr)); width: 100%; padding: 6px 8px; background: #fff; box-shadow: 0 6px 18px rgba(25,53,92,.07); }
.tabs button { position: relative; padding: 13px 2px; color: #7b8798; font-size: 10px; }
.tabs button.active { color: #1e62b5; font-weight: 700; }
.tabs button.active::after { position: absolute; bottom: 4px; left: 35%; width: 30%; height: 2px; content: ""; background: #2a70c7; border-radius: 2px; }
main { padding: 14px; }
.article-list article { display: grid; grid-template-columns: 102px minmax(0,1fr); gap: 13px; margin-bottom: 12px; padding: 12px; background: #fff; border-radius: 17px; }
.cover { position: relative; display: flex; height: 105px; align-items: flex-end; padding: 11px; overflow: hidden; color: #fff; background: linear-gradient(145deg,#172d52,#477cba); border-radius: 13px; }
.cover::before { position: absolute; top: -30px; right: -27px; width: 90px; height: 90px; content: ""; border: 15px solid rgba(255,255,255,.12); border-radius: 50%; }
.cover span { position: relative; z-index: 1; font-size: 9px; }
.cover strong { position: absolute; top: 8px; right: 10px; color: rgba(255,255,255,.35); font-size: 22px; }
.cover.blue { background: linear-gradient(145deg,#176581,#55a9bd); }
.cover.purple { background: linear-gradient(145deg,#493f76,#957cbb); }
.cover.gold { background: linear-gradient(145deg,#75562b,#c49a55); }
.cover.has-image { background-position: center; background-size: cover; }
.cover.has-image::after { position: absolute; content: ""; inset: 0; background: linear-gradient(180deg,rgba(7,25,52,.08),rgba(7,25,52,.72)); }
.cover.has-image span,.cover.has-image strong { z-index: 2; }
.article-copy > div { display: flex; justify-content: space-between; color: #4578b7; font-size: 8px; }
.article-copy .article-meta { display: grid; grid-template-columns: auto 1fr auto; gap: 5px; align-items: center; }
.article-copy .article-views { color: #6f8fb2; text-align: center; white-space: nowrap; }
.article-copy > div small { color: #a0a9b5; }
.article-copy h2 { display: -webkit-box; margin: 8px 0 5px; overflow: hidden; font-size: 13px; line-height: 1.45; -webkit-box-orient: vertical; -webkit-line-clamp: 2; }
.article-copy p { display: -webkit-box; margin-bottom: 7px; overflow: hidden; color: #8490a0; font-size: 9px; line-height: 1.45; -webkit-box-orient: vertical; -webkit-line-clamp: 2; }
.article-copy > small { color: #a6aeba; font-size: 8px; }
.progress-card { margin-bottom: 13px; padding: 20px; color: #fff; background: linear-gradient(135deg,#245c9e,#3c82ca); border-radius: 18px; }
.progress-card > span { color: #bbdcff; font-size: 9px; }
.progress-card h2 { margin: 8px 0 17px; font-size: 18px; line-height: 1.45; }
.progress-card > div { height: 5px; overflow: hidden; background: rgba(255,255,255,.18); border-radius: 3px; }
.progress-card i { display: block; height: 100%; background: #f5d681; border-radius: 3px; transition: width .3s; }
.progress-card p { margin-top: 7px; color: rgba(255,255,255,.65); font-size: 8px; }
.course-card { display: grid; grid-template-columns: 42px minmax(0,1fr) 20px; gap: 11px; align-items: center; margin-bottom: 10px; padding: 15px; background: #fff; border-radius: 16px; }
.course-no { color: #c7d8ed; font-size: 22px; font-weight: 700; }
.course-card small { color: #4f7fb8; font-size: 8px; }
.course-card h2 { margin: 5px 0; font-size: 13px; }
.course-card p { color: #8b96a5; font-size: 9px; line-height: 1.45; }
.course-card b { color: #3475c6; font-size: 17px; }
.risk-hero { padding: 20px; margin-bottom: 12px; color: #fff; background: linear-gradient(135deg,#5d332b,#a65d43); border-radius: 18px; }
.risk-hero span { color: #edc3ae; font-size: 8px; letter-spacing: 1px; }
.risk-hero h2 { margin: 8px 0; font-size: 18px; line-height: 1.45; }
.risk-hero p { color: rgba(255,255,255,.67); font-size: 9px; line-height: 1.6; }
.risk-section > article { display: grid; grid-template-columns: 40px minmax(0,1fr); gap: 11px; margin-bottom: 10px; padding: 15px; background: #fff; border-radius: 15px; }
.risk-section > article > span { display: grid; width: 38px; height: 38px; place-items: center; color: #b06a41; font-size: 12px; font-weight: 700; background: #fff1e8; border-radius: 12px; }
.risk-section h3 { font-size: 13px; }
.risk-section article p { margin: 5px 0; color: #778497; font-size: 9px; line-height: 1.55; }
.risk-section article small { color: #b28a70; font-size: 8px; }
.suitability { padding: 17px; border: 1px solid #e4d6ca; background: #fffaf5; border-radius: 16px; }
.suitability h3 { color: #6b4734; }
.suitability p { margin: 8px 0; color: #7d6a5e; font-size: 9px; line-height: 1.7; }
.suitability button { color: #9b5d3e; font-size: 9px; }
.reader { position: fixed; z-index: 100; inset: 0; display: flex; align-items: flex-end; background: rgba(5,17,35,.55); }
.reader article { position: relative; max-height: 86vh; padding: 25px 20px 30px; overflow-y: auto; background: #fff; border-radius: 24px 24px 0 0; }
.reader article > button { position: absolute; top: 12px; right: 16px; color: #8d97a5; font-size: 26px; }
.reader article > span { color: #4779b5; font-size: 9px; }
.reader h1 { margin: 11px 25px 12px 0; font-size: 21px; line-height: 1.45; }
.reader p { margin-bottom: 13px; color: #59677a; font-size: 11px; line-height: 1.9; }
.reader .lead { padding-bottom: 13px; color: #7d8998; border-bottom: 1px solid #edf0f4; }
.reader-cover,.reader-video { display: block; width: 100%; max-height: 260px; margin: 0 0 16px; object-fit: cover; background: #071e49; border-radius: 12px; }
.reader-rich-content { color: #59677a; font-size: 11px; line-height: 1.9; overflow-wrap: anywhere; }
.reader-rich-content /deep/ img { display: block !important; width: auto !important; max-width: 100% !important; height: auto !important; margin: 10px auto; object-fit: contain; }
.reader-rich-content /deep/ video,.reader-rich-content /deep/ iframe { display: block; width: 100% !important; max-width: 100% !important; height: auto; margin: 10px auto; }
.reader-rich-content /deep/ table { display: block; max-width: 100%; overflow-x: auto; border-collapse: collapse; }
.reader-rich-content /deep/ td,.reader-rich-content /deep/ th { padding: 5px; border: 1px solid #dde3eb; }
</style>
